import { useLayoutEffect, useRef } from "react";
import { Section } from "@/shared/components/ui/Section";
import { formatBusinessDate, getBusinessDateKey } from "@/shared/utils/formatters";
import type { Message, MessageStatus } from "../types/messageTypes";

const statusLabels: Record<MessageStatus, string> = {
  queued: "Queued",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
  received: "Received",
};

export function MessagesList({
  messages,
  customerName,
  timeZone,
}: {
  messages: Message[];
  customerName: string;
  timeZone: string;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const lastMessageId = messages.at(-1)?.id;
  useLayoutEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [lastMessageId]);

  const days = new Map<string, Message[]>();
  for (const message of messages) {
    const day = getBusinessDateKey(message.createdAt, timeZone);
    const group = days.get(day) || [];
    group.push(message);
    days.set(day, group);
  }

  return (
    <div className="space-y-[26px]">
      {Array.from(days, ([day, group]) => (
        <Section
          key={day}
          title={formatBusinessDate(group[0].createdAt, timeZone, {
            weekday: "long", day: "numeric", month: "long", year: "numeric",
          })}
          count="Oldest first"
        >
          <ol className="messages-timeline" aria-label={`Message history for ${day}`}>
            {group.map((message) => (
              <li key={message.id} className={`messages-bubble${message.direction === "outbound" ? " messages-bubble-outbound" : ""}`}>
                <p className="messages-meta">
                  {message.direction === "outbound" ? "You" : customerName} ·{" "}
                  <time dateTime={message.createdAt.toISOString()}>
                    {formatBusinessDate(message.createdAt, timeZone, { hour: "2-digit", minute: "2-digit" })}
                  </time>
                </p>
                <p className="messages-body">{message.body}</p>
                <p className={`messages-meta${message.status === "failed" ? " messages-delivery-failed" : ""}`}>
                  {statusLabels[message.status]}
                </p>
              </li>
            ))}
          </ol>
        </Section>
      ))}
      <div ref={endRef} />
    </div>
  );
}
